import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { format, addDays } from 'date-fns';

// Industry-standard DOOD status codes with colors and descriptions
const STATUS_CODES = {
  SW: { 
    label: 'SW', 
    fullName: 'Start Work', 
    description: "Actor's first day working on set",
    bgColor: '#4caf50',
    textColor: '#fff'
  },
  W: { 
    label: 'W', 
    fullName: 'Work', 
    description: 'Actor is working after start',
    bgColor: '#4caf50',
    textColor: '#fff'
  },
  WF: { 
    label: 'WF', 
    fullName: 'Work Finish', 
    description: "Actor's last day on the job",
    bgColor: '#4caf50',
    textColor: '#fff'
  },
  SWF: { 
    label: 'SWF', 
    fullName: 'Start-Work-Finish', 
    description: 'Actor is only needed one day of the shoot',
    bgColor: '#8bc34a',
    textColor: '#fff'
  },
  H: { 
    label: 'H', 
    fullName: 'Hold', 
    description: "Actor isn't needed but is still on call and paid",
    bgColor: '#ff9800',
    textColor: '#fff'
  },
  I: { 
    label: 'I', 
    fullName: 'Idle', 
    description: 'Like a Hold but not paid',
    bgColor: '#ffeb3b',
    textColor: '#333'
  },
  T: { 
    label: 'T', 
    fullName: 'Travel', 
    description: 'Actor is traveling',
    bgColor: '#2196f3',
    textColor: '#fff'
  },
  R: { 
    label: 'R', 
    fullName: 'Rehearsal', 
    description: 'Actor is called to rehearse, but not shoot',
    bgColor: '#9c27b0',
    textColor: '#fff'
  },
  WD: { 
    label: 'WD', 
    fullName: 'Work-Drop', 
    description: "Actor's last day before a 7+ day hiatus",
    bgColor: '#f44336',
    textColor: '#fff'
  },
  PW: { 
    label: 'PW', 
    fullName: 'Pickup-Work', 
    description: 'Actor comes back from 7+ day hiatus',
    bgColor: '#00bcd4',
    textColor: '#fff'
  },
  PWF: { 
    label: 'PWF', 
    fullName: 'Pickup-Work-Finish', 
    description: "Actor's first and last day back after hiatus",
    bgColor: '#00bcd4',
    textColor: '#fff'
  },
  SR: { 
    label: 'SR', 
    fullName: 'Start-Rehearsal', 
    description: "Actor is rehearsing and it's their first day",
    bgColor: '#e91e63',
    textColor: '#fff'
  }
};

// Day of week abbreviations
const DAY_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function DayOutOfDaysReport({ schedule, projectName = 'Production', onScheduleUpdate }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // State for editing
  const [editedSchedule, setEditedSchedule] = useState(schedule);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { characterId, dayIndex, shootingDay, scheduleDayIndex }
  
  // Use edited schedule if available, otherwise use prop
  const activeSchedule = editedSchedule || schedule;

  useEffect(() => {
    setEditedSchedule(schedule);
  }, [schedule]);
  
  // Handle cell click to open status picker
  const handleCellClick = useCallback((event, characterId, dayIndex, shootingDay, scheduleDayIndex) => {
    setMenuAnchor(event.currentTarget);
    setEditingCell({ characterId, dayIndex, shootingDay, scheduleDayIndex });
  }, []);
  
  // Handle status selection
  const handleStatusSelect = useCallback((newStatus) => {
    if (!editingCell) return;
    
    const { characterId, shootingDay, scheduleDayIndex } = editingCell;
    
    // Clone the schedule
    const updatedSchedule = JSON.parse(JSON.stringify(activeSchedule));
    
    // Find or create the day entry
    let dayEntry = updatedSchedule.days[scheduleDayIndex];
    if (!dayEntry) {
      // fallback: find by day number
      dayEntry = updatedSchedule.days.find(d => d.day === shootingDay);
    }
    if (!dayEntry) return;
    
    // Find or create the character status entry
    let charStatus = dayEntry.characterStatuses.find(cs => cs.characterId === characterId);
    if (newStatus === null) {
      // Remove status
      dayEntry.characterStatuses = dayEntry.characterStatuses.filter(cs => cs.characterId !== characterId);
    } else if (charStatus) {
      charStatus.status = newStatus;
    } else {
      dayEntry.characterStatuses.push({ characterId, status: newStatus });
    }
    
    setEditedSchedule(updatedSchedule);
    setMenuAnchor(null);
    setEditingCell(null);
    
    // Notify parent of update with edit type and data
    if (onScheduleUpdate) {
      onScheduleUpdate(updatedSchedule, 'status', {
        characterId,
        dayNum: dayEntry.day,
        status: newStatus
      });
    }
  }, [editingCell, activeSchedule, onScheduleUpdate]);
  
  // Close menu
  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setEditingCell(null);
  }, []);

  // Helper to safely parse a date string (YYYY-MM-DD format)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Parse YYYY-MM-DD format in local time
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  // Generate shooting days data with dates
  // Days marked as isOff (from one-liner gaps like weekends) are greyed out
  // Days can also be manually toggled off via offDays array
  const shootingData = useMemo(() => {
    if (!activeSchedule?.days) return { days: [], offDays: new Set() };
    
    const days = [];
    const offDays = new Set();
    let shootingDayNum = 1;
    
    // Build days array directly from schedule data
    // Each day already has isOff flag set from generation (based on one-liner dates)
    activeSchedule.days.forEach((scheduleDay, idx) => {
      const date = parseDate(scheduleDay.date);
      const dayOfWeek = date ? date.getDay() : 0;
      
      // Check if day is off (either from one-liner gaps or manually marked)
      const isOffFromSchedule = scheduleDay.isOff === true;
      const isManuallyOff = activeSchedule.offDays?.includes(scheduleDay.day);
      const isOff = isOffFromSchedule || isManuallyOff;
      
      if (isOff) {
        offDays.add(idx);
        days.push({
          index: idx,
          date,
          dayOfWeek: date ? DAY_ABBREV[dayOfWeek] : '',
          shootingDay: scheduleDay.day || 'off',
          isOff: true,
          characterStatuses: scheduleDay.characterStatuses || []
        });
      } else {
        days.push({
          index: idx,
          date,
          dayOfWeek: date ? DAY_ABBREV[dayOfWeek] : '',
          shootingDay: scheduleDay.day || shootingDayNum,
          isOff: false,
          characterStatuses: scheduleDay.characterStatuses || []
        });
        shootingDayNum++;
      }
    });
    
    return { days, offDays };
  }, [activeSchedule]);

  // Calculate summary stats for each character
  const characterStats = useMemo(() => {
    if (!activeSchedule?.characters) return {};
    
    const stats = {};
    activeSchedule.characters.forEach(char => {
      const charId = char.id || char._id;
      stats[charId] = { work: 0, hold: 0, travel: 0, total: 0 };
      
      shootingData.days.forEach(day => {
        if (day.isOff) return;
        
        const charStatus = day.characterStatuses?.find(
          cs => cs.characterId === charId
        );
        const status = charStatus?.status;
        
        if (status) {
          stats[charId].total++;
          if (['W', 'SW', 'WF', 'SWF', 'WD', 'PW', 'PWF'].includes(status)) {
            stats[charId].work++;
          } else if (status === 'H') {
            stats[charId].hold++;
          } else if (status === 'T') {
            stats[charId].travel++;
          }
        }
      });
    });
    
    return stats;
  }, [activeSchedule, shootingData]);

  if (!activeSchedule || !activeSchedule.characters || !activeSchedule.days) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No schedule data available.
        </Typography>
      </Box>
    );
  }

  const { characters } = activeSchedule;
  const { days } = shootingData;

  // Styles
  const headerBg = isDarkMode ? '#1a3a4a' : '#2d5a6b';
  const headerText = '#fff';
  const rowBg = isDarkMode ? '#1e1e1e' : '#fff';
  const altRowBg = isDarkMode ? '#2a2a2a' : '#f5f5f5';
  const borderColor = isDarkMode ? '#444' : '#ddd';
  const offDayBg = isDarkMode ? '#333' : '#e0e0e0';

  const cellStyle = {
    padding: '4px 8px',
    textAlign: 'center',
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    fontSize: '12px',
    minWidth: '45px',
    whiteSpace: 'nowrap'
  };

  const headerCellStyle = {
    ...cellStyle,
    backgroundColor: headerBg,
    color: headerText,
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 2
  };

  const firstColStyle = {
    ...cellStyle,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    minWidth: '150px',
    textAlign: 'left',
    fontWeight: 'bold'
  };

  const renderStatusCell = (status, isOff, key, characterId, dayIndex, shootingDay, scheduleDayIndex) => {
    const statusInfo = status ? STATUS_CODES[status] : null;
    const baseStyle = {
      ...cellStyle,
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      backgroundColor: isOff ? offDayBg : undefined
    };

    return (
      <td 
        key={key} 
        style={baseStyle}
        onClick={(e) => handleCellClick(e, characterId, dayIndex, shootingDay, scheduleDayIndex)}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#e3f2fd'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isOff ? offDayBg : ''; }}
      >
        {statusInfo ? (
          <Tooltip title={`${statusInfo.fullName}: ${statusInfo.description} (click to change)`} arrow>
            <Box
              sx={{
                display: 'inline-block',
                px: 0.75,
                py: 0.25,
                borderRadius: '4px',
                backgroundColor: statusInfo.bgColor,
                color: statusInfo.textColor,
                fontWeight: 'bold',
                fontSize: '11px'
              }}
            >
              {statusInfo.label}
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={isOff ? "Off day – click to add status" : "Click to add status"} arrow>
            <Box sx={{ 
              width: '100%', 
              height: '20px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: isDarkMode ? '#666' : '#999',
              fontSize: '12px',
              fontWeight: isOff ? 700 : 400
            }}>
              {isOff ? 'OFF +' : '+'}
            </Box>
          </Tooltip>
        )}
      </td>
    );
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        overflow: 'hidden',
        borderRadius: 2
      }}
    >
      {/* Report Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          backgroundColor: headerBg,
          color: headerText
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Day Out Of Days Report
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 2 }}>
          {projectName.toUpperCase()}
        </Typography>
      </Box>

      {/* Table Container */}
      <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            {/* Month/Day Row */}
            <tr>
              <th style={{ ...headerCellStyle, left: 0, zIndex: 3 }}></th>
              <th style={headerCellStyle}>Month / Day</th>
              {days.map((day, idx) => (
                <th key={`date-${idx}`} style={{
                  ...headerCellStyle,
                  backgroundColor: day.isOff ? offDayBg : headerBg,
                  color: day.isOff ? (isDarkMode ? '#999' : '#666') : headerText
                }}>
                  {day.date ? (() => {
                  let d;
                  if (typeof day.date === 'string') {
                    const [year, month, dayVal] = day.date.split('-').map(Number);
                    d = new Date(year, month - 1, dayVal);
                  } else {
                    d = day.date;
                  }
                  return format(d, 'M/dd');
                })() : '-'}
                </th>
              ))}
              <th style={headerCellStyle}>Work</th>
              <th style={headerCellStyle}>Hold</th>
              <th style={headerCellStyle}>Travel</th>
              <th style={headerCellStyle}>Total</th>
            </tr>

            {/* Day of Week Row */}
            <tr>
              <th style={{ ...headerCellStyle, left: 0, zIndex: 3 }}></th>
              <th style={headerCellStyle}>Day of Week</th>
              {days.map((day, idx) => (
                <th key={`dow-${idx}`} style={{
                  ...headerCellStyle,
                  backgroundColor: day.isOff ? offDayBg : headerBg,
                  color: day.isOff ? (isDarkMode ? '#999' : '#666') : headerText
                }}>
                  {day.dayOfWeek}
                </th>
              ))}
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
            </tr>

            {/* Shooting Day Row */}
            <tr>
              <th style={{ ...headerCellStyle, left: 0, zIndex: 3 }}></th>
              <th style={headerCellStyle}>
                <span>Shooting Day</span>
              </th>
              {days.map((day, idx) => {
                return (
                  <th 
                    key={`sd-${idx}`} 
                    style={{
                      ...headerCellStyle,
                      backgroundColor: day.isOff ? offDayBg : headerBg,
                      color: day.isOff ? (isDarkMode ? '#999' : '#666') : headerText
                    }}
                  >
                    <span>{day.isOff ? 'OFF' : day.shootingDay}</span>
                  </th>
                );
              })}
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
              <th style={headerCellStyle}></th>
            </tr>
          </thead>

          <tbody>
            {characters.map((character, charIdx) => {
              const charId = character.id || character._id;
              const stats = characterStats[charId] || { work: 0, hold: 0, travel: 0, total: 0 };
              const bgColor = charIdx % 2 === 0 ? rowBg : altRowBg;

              return (
                <tr key={charId}>
                  {/* Row Number */}
                  <td style={{ 
                    ...firstColStyle, 
                    backgroundColor: bgColor,
                    minWidth: '40px',
                    textAlign: 'center'
                  }}>
                    {charIdx + 1}
                  </td>

                  {/* Character Name */}
                  <td style={{ 
                    ...firstColStyle, 
                    backgroundColor: bgColor,
                    left: '40px'
                  }}>
                    {character.name}
                    {character.role && (
                      <Typography 
                        component="span" 
                        sx={{ 
                          display: 'block',
                          fontSize: '10px', 
                          color: 'text.secondary',
                          fontWeight: 'normal'
                        }}
                      >
                        {character.role}
                      </Typography>
                    )}
                  </td>

                  {/* Status Cells */}
                  {days.map((day, dayIdx) => {
                    const charStatus = day.characterStatuses?.find(
                      cs => cs.characterId === charId
                    );
                    return renderStatusCell(
                      charStatus?.status,
                      day.isOff,
                      `${charId}-${dayIdx}`,
                      charId,
                      dayIdx,
                      day.shootingDay,
                      day.index ?? dayIdx
                    );
                  })}

                  {/* Summary Columns */}
                  <td style={{ ...cellStyle, backgroundColor: bgColor, fontWeight: 'bold' }}>
                    {stats.work}
                  </td>
                  <td style={{ ...cellStyle, backgroundColor: bgColor, fontWeight: 'bold' }}>
                    {stats.hold}
                  </td>
                  <td style={{ ...cellStyle, backgroundColor: bgColor, fontWeight: 'bold' }}>
                    {stats.travel}
                  </td>
                  <td style={{ 
                    ...cellStyle, 
                    backgroundColor: bgColor, 
                    fontWeight: 'bold',
                    borderRight: 'none'
                  }}>
                    {stats.total}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {/* Legend */}
      <Box sx={{ p: 2, borderTop: `1px solid ${borderColor}`, backgroundColor: isDarkMode ? '#1a1a1a' : '#fafafa' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Status Code Legend (click any cell to edit):
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(STATUS_CODES).map(([code, info]) => (
            <Tooltip key={code} title={info.description} arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: isDarkMode ? '#333' : '#eee',
                  fontSize: '11px',
                  cursor: 'help'
                }}
              >
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: info.bgColor,
                    color: info.textColor,
                    fontWeight: 'bold',
                    fontSize: '10px'
                  }}
                >
                  {code}
                </Box>
                <span>{info.fullName}</span>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Status Picker Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 280
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold', color: 'text.secondary' }}>
          Select Status
        </Typography>
        <Divider />
        
        {/* Work statuses */}
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
          Work
        </Typography>
        {['SW', 'W', 'WF', 'SWF'].map(code => {
          const info = STATUS_CODES[code];
          return (
            <MenuItem key={code} onClick={() => handleStatusSelect(code)} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ 
                  px: 0.75, py: 0.25, borderRadius: '4px', 
                  backgroundColor: info.bgColor, color: info.textColor,
                  fontWeight: 'bold', fontSize: '11px'
                }}>
                  {code}
                </Box>
              </ListItemIcon>
              <ListItemText primary={info.fullName} secondary={info.description} 
                primaryTypographyProps={{ fontSize: '13px' }}
                secondaryTypographyProps={{ fontSize: '11px' }}
              />
            </MenuItem>
          );
        })}
        
        <Divider sx={{ my: 0.5 }} />
        
        {/* Hiatus statuses */}
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
          Hiatus / Pickup
        </Typography>
        {['WD', 'PW', 'PWF'].map(code => {
          const info = STATUS_CODES[code];
          return (
            <MenuItem key={code} onClick={() => handleStatusSelect(code)} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ 
                  px: 0.75, py: 0.25, borderRadius: '4px', 
                  backgroundColor: info.bgColor, color: info.textColor,
                  fontWeight: 'bold', fontSize: '11px'
                }}>
                  {code}
                </Box>
              </ListItemIcon>
              <ListItemText primary={info.fullName} secondary={info.description}
                primaryTypographyProps={{ fontSize: '13px' }}
                secondaryTypographyProps={{ fontSize: '11px' }}
              />
            </MenuItem>
          );
        })}
        
        <Divider sx={{ my: 0.5 }} />
        
        {/* Other statuses */}
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
          Other
        </Typography>
        {['H', 'I', 'T', 'R', 'SR'].map(code => {
          const info = STATUS_CODES[code];
          return (
            <MenuItem key={code} onClick={() => handleStatusSelect(code)} dense>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ 
                  px: 0.75, py: 0.25, borderRadius: '4px', 
                  backgroundColor: info.bgColor, color: info.textColor,
                  fontWeight: 'bold', fontSize: '11px'
                }}>
                  {code}
                </Box>
              </ListItemIcon>
              <ListItemText primary={info.fullName} secondary={info.description}
                primaryTypographyProps={{ fontSize: '13px' }}
                secondaryTypographyProps={{ fontSize: '11px' }}
              />
            </MenuItem>
          );
        })}
        
        <Divider sx={{ my: 0.5 }} />
        
        {/* Clear option */}
        <MenuItem onClick={() => handleStatusSelect(null)} dense sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
            ✕
          </ListItemIcon>
          <ListItemText primary="Clear Status" primaryTypographyProps={{ fontSize: '13px' }} />
        </MenuItem>
      </Menu>
    </Paper>
  );
}

export default DayOutOfDaysReport;
